
import React, { useEffect } from 'react';
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// Denied access sends you home — but "/" resumes the last route, which would
// be the denied page itself. Clear the saved route first so it can't loop.
function DeniedHome() {
  useEffect(() => {
    try { localStorage.removeItem("lastRoute"); } catch { /* ignore */ }
  }, []);
  return <Navigate to="/" replace />;
}

// anySignedIn: every signed-in account may open it, whatever its roles
// (Change password, Jill 2026-11-04).
export default function ProtectedRoute({ children, allowedRoles = [], allowedEmails = [], allowedPositions = [], anySignedIn = false }) {
  const { roles, boardPosition, committeePositions = [], positionLoaded, loading, session, user } = useAuth();
  const location = useLocation();

  if (loading) {
    // AuthContext handles the main "Loading..." screen, 
    // but individual protection checks shouldn't run until ready.
    return null;
  }

  if (!session) {
    // Not logged in -> Go to Login
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (anySignedIn) return children;

  const normalizedUserRoles = (roles || []).map((r) => String(r).toLowerCase());

  // 1. Admin Override
  if (normalizedUserRoles.includes("system_admin")) {
    return children;
  }

  // 2. Email Override (Specific users)
  if (allowedEmails.length > 0 && user?.email && allowedEmails.includes(user.email)) {
    return children;
  }

  // 3. Position Override — the board seat (president, secretary, …) OR any
  //    training-committee position (training_chair, ce_organizer, auditor, …).
  //    A committee position counts only from committeePositions (the roster
  //    checkbox); a board_position naming one grants nothing (Jill 2026-10-07).
  if (allowedPositions.length > 0) {
    const allowed = allowedPositions.map((p) => String(p).toLowerCase());
    //    Board seats, plus the two legacy CE values some roster rows carried
    //    before CE moved to committee positions. Nothing else.
    const seat = ["president", "vice_president", "secretary", "member", "ce_lead", "ce_organizer"]
      .includes(String(boardPosition || "").toLowerCase()) ? boardPosition : null;
    const held = [seat, ...(committeePositions || [])]
      .filter(Boolean)
      .map((p) => String(p).toLowerCase());
    if (held.some((p) => allowed.includes(p))) return children;
  }

  // 4. Check specific roles
  const hasAccess = allowedRoles.some((role) =>
    normalizedUserRoles.includes(String(role).toLowerCase())
  );

  if (!hasAccess) {
    // A position could still grant access but the async position lookup hasn't
    // answered yet — wait instead of bouncing (deep links to position-gated
    // pages like the Training Chair digest denied prematurely otherwise).
    if (allowedPositions.length > 0 && !positionLoaded) return null;
    console.warn("Access denied", { path: location.pathname, roles: normalizedUserRoles });
    return <DeniedHome />;
  }

  return children;
}